/** Scenes for looking at the art and the flight paths (docs/spec/05 §5): `?scene=sprites`, `?scene=paths`. */

import { CYAN, DARK_GREY, GREY, LIME, RED, WHITE, YELLOW } from '../render/palette';
import type { Screen } from '../render/screen';
import type { SpriteId } from '../render/sprites';
import { CAPSULE_LOOK, drawCapsule, drawSprite, UP } from '../render/sprites';
import { Starfield } from '../render/starfield';
import type { CapsuleKind } from '../sim';
import { allPaths, FIELD, FORMATION } from '../sim';
import type { MenuAction, Scene } from '../ui/scene';

const FLYERS: readonly SpriteId[] = ['lancer', 'captive', 'stinger', 'moth', 'monarch', 'monarchHit', 'scarab', 'mantis', 'hornet'];
const BADGES: readonly SpriteId[] = ['badge1', 'badge5', 'badge10', 'badge20', 'badge30', 'badge50'];

/** Every sprite: flyers at eight headings and spinning, the Empress, explosions frame by frame, the small things. */
export function spritesScene(): Scene {
  let ms = 0;
  const stars = new Starfield(3);
  return {
    update(dt) {
      ms += dt;
      stars.update(dt);
    },
    draw(screen: Screen) {
      screen.clear(0);
      stars.draw(screen, true);
      const frame = Math.floor(ms / 400);
      FLYERS.forEach((id, i) => {
        const y = 14 + i * 19;
        for (let k = 0; k < 8; k++) drawSprite(screen, id, frame, 14 + k * 20, y, UP + (k * Math.PI) / 4);
        drawSprite(screen, id, frame, 180, y, UP + ms / 300);
        drawSprite(screen, id, frame + 1, 204, y);
      });
      drawSprite(screen, 'empress', frame, 40, 208);
      drawSprite(screen, 'empressAngry', frame, 112, 208);
      for (let f = 0; f < 5; f++) drawSprite(screen, 'boom', f, 160 + (f % 3) * 24, 196 + Math.floor(f / 3) * 24);
      for (let f = 0; f < 6; f++) drawSprite(screen, 'bigBoom', f, 20 + f * 34, 252);
      (Object.keys(CAPSULE_LOOK) as CapsuleKind[]).forEach((kind, i) => drawCapsule(screen, kind, 12 + i * 17, 278, ms / 16));
      BADGES.forEach((id, i) => drawSprite(screen, id, 0, 136 + i * 10, 278));
      drawSprite(screen, 'shot', 0, 204, 274);
      drawSprite(screen, 'shotPierce', 0, 212, 274);
      drawSprite(screen, 'bomb', frame, 220, 274);
    },
  };
}

const PATH_COLOURS = [CYAN, YELLOW, LIME, RED, WHITE];

/** Every authored path, one at a time or all at once (left/right to step), with the formation's slots. */
export function pathsScene(): Scene {
  const paths = allPaths();
  let sel = -1;
  let ms = 0;
  return {
    update(dt) {
      ms += dt;
    },
    action(a: MenuAction) {
      if (a === 'right' || a === 'confirm') sel = sel + 1 >= paths.length ? -1 : sel + 1;
      if (a === 'left') sel = sel - 1 < -1 ? paths.length - 1 : sel - 1;
    },
    draw(screen: Screen) {
      screen.clear(0);
      FORMATION.rows.forEach(([first, last], row) => {
        for (let col = first; col <= last; col++) {
          screen.box(FORMATION.cx + (col - 4.5) * FORMATION.cell - 6, FORMATION.top + row * FORMATION.cell - 6, 12, 12, DARK_GREY);
        }
      });
      screen.fill(0, FIELD.fighterY, FIELD.w, 1, DARK_GREY);
      paths.forEach(({ path }, i) => {
        if (sel >= 0 && sel !== i) return;
        const colour = PATH_COLOURS[i % PATH_COLOURS.length] ?? WHITE;
        for (let k = 0; k < path.xs.length; k += 3) screen.fill(path.xs[k] ?? 0, path.ys[k] ?? 0, 1, 1, colour);
        // A marker flying it at 3 px a tick.
        const d = Math.floor((ms * 0.18) % path.xs.length);
        drawSprite(screen, 'stinger', 0, path.xs[d] ?? 0, path.ys[d] ?? 0, path.hs[d] ?? UP);
      });
      screen.text(sel < 0 ? 'ALL PATHS' : (paths[sel]?.name ?? '').toUpperCase(), 4, 276, GREY);
    },
  };
}
